import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getFeaturedArticles, getHotArticles, articles } from '@/mocks/articles';

function HomeHero() {
  return (
    <section className="relative w-full h-[520px] md:h-[600px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Elegant%20modern%20digital%20content%20platform%20hero%20background%2C%20warm%20crimson%20and%20amber%20gradient%20atmosphere%2C%20abstract%20flowing%20geometric%20shapes%2C%20soft%20cream%20undertones%2C%20sophisticated%20minimalist%20aesthetic%2C%20premium%20editorial%20style%2C%20luxurious%20warm%20lighting%2C%20clean%20composition%2C%20professional%20media%20platform%20vibe&width=1600&height=900&seq=home-hero-2026&orientation=landscape"
        alt="日本伦理d3tt官方资讯平台"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
      <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4 leading-tight">
          日本伦理<span className="text-accent-300">d3tt</span> 官方资讯平台
        </h1>
        <p className="text-sm md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
          日本伦理d3tt是当前热门搜索内容，本平台为您提供最新最全的日本伦理d3tt相关资讯、背景知识与获取渠道，帮助您快速了解日本伦理d3tt的核心内容与使用方式。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-compass-3-line" />
            开始探索
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-information-line" />
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="w-full bg-background-50 border-b border-background-200/70">
      <div className="w-full px-4 md:px-6 py-5">
        <div className="flex items-center justify-center flex-wrap gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to="/category"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-600 text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className={`${cat.icon} text-sm`} />
              {cat.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = getFeaturedArticles();

  return (
    <section className="w-full bg-background-50 py-12 md:py-16">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
              精选推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">编辑精选的优质日本伦理d3tt内容</p>
          </div>
          <Link
            to="/category"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            查看全部
            <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {featured.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-300 cursor-pointer"
            >
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-primary-500 text-white font-medium">
                    精选
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {article.views.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    {article.readTime}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latestArticles = articles.slice(0, 6);

  return (
    <section className="w-full bg-background-100 py-12 md:py-16">
      <div className="w-full px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  最新资讯
                </h2>
                <p className="text-sm text-foreground-500 mt-1">日本伦理d3tt最新动态与内容更新</p>
              </div>
              <Link
                to="/category"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                更多文章
                <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="flex gap-4 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200/50 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1.5">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 sticky top-20">
              <h3 className="text-base font-bold font-heading text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" />
                热门排行
              </h3>
              <div className="space-y-3">
                {getHotArticles(5).map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="flex items-start gap-3 cursor-pointer group"
                  >
                    <span className={`text-lg font-bold flex-shrink-0 w-6 text-center ${
                      index < 3 ? 'text-primary-500' : 'text-foreground-300'
                    }`}>
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 leading-relaxed">
                        {article.title}
                      </p>
                      <span className="text-xs text-foreground-400 mt-1 inline-block">
                        {article.views.toLocaleString()} 次浏览
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      q: '日本伦理d3tt是什么？',
      a: '日本伦理d3tt是一个综合性的内容资讯平台，汇聚了丰富的相关资源与信息，为用户提供便捷的内容浏览和获取体验。平台涵盖最新资讯、精选合集、在线资源等多种内容类型。',
    },
    {
      q: '日本伦理d3tt如何获取最新内容？',
      a: '您可以通过日本伦理d3tt首页的精选推荐和最新资讯模块获取每日更新的内容。此外，分类浏览页面支持按不同标签筛选，帮助您快速找到感兴趣的日本伦理d3tt资源。',
    },
    {
      q: '日本伦理d3tt是否支持移动端访问？',
      a: '是的，日本伦理d3tt平台针对移动设备进行了全面优化，支持手机和平板浏览器直接访问。界面会自动适配不同屏幕尺寸，提供流畅的移动端浏览体验。',
    },
    {
      q: '日本伦理d3tt的内容多久更新一次？',
      a: '日本伦理d3tt平台保持每日更新频率，编辑团队持续筛选和发布优质内容。建议每日访问以获取最新的日本伦理d3tt资讯和资源推荐。',
    },
    {
      q: '如何联系日本伦理d3tt团队？',
      a: '您可以通过关于我们页面的联系方式与我们取得联系。日本伦理d3tt团队重视每一位用户的反馈和建议，将在收到消息后尽快回复。',
    },
  ];

  return (
    <section className="w-full bg-background-50 py-12 md:py-16">
      <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 mt-1">关于日本伦理d3tt的常见疑问解答</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <details key={index} className="group bg-background-100 rounded-lg border border-background-200/70">
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                <i className="ri-add-line text-foreground-400 group-open:hidden text-lg flex-shrink-0" />
                <i className="ri-subtract-line text-foreground-400 hidden group-open:block text-lg flex-shrink-0" />
              </summary>
              <div className="px-5 pb-4">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const homeSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '日本伦理d3tt官方落地页 - 首页',
      description: '日本伦理d3tt是当前热门搜索内容，本页为您详细介绍日本伦理d3tt的相关资讯、背景知识与获取渠道。',
      url: 'https://jp-ethics-d3tt.readdy.site/',
      isPartOf: {
        '@type': 'WebSite',
        name: '日本伦理d3tt官方资讯平台',
        url: 'https://jp-ethics-d3tt.readdy.site/',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '日本伦理d3tt是什么？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '日本伦理d3tt是一个综合性的内容资讯平台，汇聚了丰富的相关资源与信息，为用户提供便捷的内容浏览和获取体验。',
          },
        },
        {
          '@type': 'Question',
          name: '日本伦理d3tt如何获取最新内容？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过日本伦理d3tt首页的精选推荐和最新资讯模块获取每日更新的内容。',
          },
        },
        {
          '@type': 'Question',
          name: '日本伦理d3tt是否支持移动端访问？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '是的，日本伦理d3tt平台针对移动设备进行了全面优化，支持手机和平板浏览器直接访问。',
          },
        },
        {
          '@type': 'Question',
          name: '日本伦理d3tt的内容多久更新一次？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '日本伦理d3tt平台保持每日更新频率，编辑团队持续筛选和发布优质内容。',
          },
        },
        {
          '@type': 'Question',
          name: '如何联系日本伦理d3tt团队？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过关于我们页面的联系方式与我们取得联系。',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title="日本伦理d3tt官方落地页 - 日本伦理d3tt资源、在线、最新合集介绍"
        description="日本伦理d3tt是当前热门搜索内容，本页为您详细介绍日本伦理d3tt的相关资讯、背景知识与获取渠道，帮助用户快速了解日本伦理d3tt的核心内容与使用方式，满足您对日本伦理d3tt的全面需求。"
        keywords="日本伦理d3tt,日本伦理d3tt资源,日本伦理d3tt在线,日本伦理d3tt最新,日本伦理d3tt合集,日本伦理d3tt下载,日本伦理d3tt介绍"
        canonicalUrl="https://jp-ethics-d3tt.readdy.site/"
        schemaData={homeSchema}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main>
          <HomeHero />
          <CategoryNav />
          <FeaturedSection />
          <LatestSection />
          <FAQSection />
        </main>

        <Footer />
      </div>
    </>
  );
}